(() => {
  // This page owns one cinematic section. A Shopify section replacement must
  // retire the old controller before binding the replacement's controls.
  const controllers = new Map();
  function mount(root) {
    if (controllers.has(root)) return;
    const cleanups = [], timers = new Set(), frames = new Set();
    let destroyed = false;
    const listen = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };
    const later = (callback, delay) => {
      const id = setTimeout(() => { timers.delete(id); if (!destroyed) callback(); }, delay);
      timers.add(id); return id;
    };
    const frame = callback => {
      const id = requestAnimationFrame(() => { frames.delete(id); if (!destroyed) callback(); });
      frames.add(id); return id;
    };
    const editor = !!window.Shopify?.designMode;
    const observersAvailable = typeof IntersectionObserver === 'function' && typeof ResizeObserver === 'function';
    const $ = (s, scope = root) => scope.querySelector(s);
    const $$ = (s, scope = root) => [...scope.querySelectorAll(s)];
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const guidedScroll = new URLSearchParams(location.search).get('scroll') !== 'original';
    const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
    const desktop = matchMedia('(min-width: 1024px) and (min-height: 620px) and (hover: hover) and (pointer: fine)');
    const header = $('.site-header'), menu = $('#chapter-dialog'), credits = $('#credits-dialog');
    const viewer = $('#image-dialog'), menuButton = $('.index-button'), motionButton = $('#menu-motion-toggle');
    const photoGallery = $('#parish-photo-dialog'), photoGalleryButton = $('.pw-gallery-open');
    const wallStyleSelect = $('#photo-wall-style');
    const prayer = $('#prayer-text'), prayerButton = $('.prayer-button');
    const panels = $$('.journey-panel');
    const read = key => { try { return localStorage.getItem(key); } catch { return null; } };
    const save = (key, value) => { try { localStorage.setItem(key, value); } catch {} };
    let wallStyle = read('st-raphael-wall-style') || 'immersive';
    wallStyleSelect.value = wallStyle;
    let motion = !editor && observersAvailable && read('st-raphael-story-motion') !== 'off' && !reduced.matches;
    let context, cinematic = true, chapter = 0, resizeTimer, overflow = '', locked = false;
    let sceneBookmark = null, resizeBookmark = null, resizing = false;
    let viewBookmark = null, resizeViewBookmark = null, viewFrame = 0;
    let origin, openingImage = false, closing = false, menuAnimation, imageRequest = 0;
    // Guide only recent mouse-wheel/trackpad input. Native touch, keyboard,
    // links, dialogs and layout restoration never initiate an automatic settle.
    let wheelAt = 0, wheelDirection = 0;
    function cancelGuidance() {
      wheelAt = 0;
      window.ScrollTrigger?.getAll().forEach(t => t.getTween(true)?.kill?.());
    }
    listen(window, 'wheel', event => {
      cancelGuidance();
      if (!guidedScroll || !motion || !finePointer.matches || event.ctrlKey || event.metaKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX) || document.querySelector('dialog[open]')) return;
      wheelAt = performance.now(); wheelDirection = Math.sign(event.deltaY);
    }, { passive: true, capture: true });
    listen(window, 'pointerdown', cancelGuidance, { passive: true });
    listen(window, 'touchstart', cancelGuidance, { passive: true });
    listen(window, 'keydown', cancelGuidance);
    listen(window, 'blur', cancelGuidance);
    listen(document, 'visibilitychange', cancelGuidance);
    function softSettle(points) {
      if (!guidedScroll || !finePointer.matches) return false;
      return {
        inertia: false, directional: false, delay: .24,
        duration: { min: .22, max: .5 }, ease: 'power2.out',
        snapTo(value, trigger) {
          if (!wheelAt || performance.now() - wheelAt > 2200 || !motion || reduced.matches || resizing || document.hidden || document.querySelector('dialog[open]') || !trigger.isActive) return value;
          const position = trigger.progress;
          const radius = Math.min(.055, 100 / (trigger.end - trigger.start));
          const target = points.find(point => Math.abs(point - position) <= radius && (point - position) * wheelDirection > .001);
          return target ?? value;
        }
      };
    }
    const responseTime = (original, desktopTime, touchTime) => guidedScroll ? (finePointer.matches ? desktopTime : touchTime) : original;
    const lock = () => { if (!locked) overflow = document.body.style.overflow; locked = true; document.body.style.overflow = 'hidden'; };
    const unlock = () => { if (locked) document.body.style.overflow = overflow; locked = false; };

    $$('.journey-art').forEach(figure => {
      if ($('.art-frame', figure)) return;
      const img = $('img', figure), button = document.createElement('button');
      button.type = 'button'; button.className = 'art-frame';
      button.dataset.viewImage = img.dataset.fullImage || img.getAttribute('src');
      button.dataset.caption = $('figcaption', figure).textContent;
      button.setAttribute('aria-label', `Enlarge artwork: ${img.alt}`);
      img.before(button); button.append(img);
      cleanups.push(() => { button.before(img); button.remove(); });
    });
    if (typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(([entry]) => {
        header.classList.toggle('is-reading', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { rootMargin: '-104px 0px 0px 0px' });
      observer.observe($('.sr-story-hero'));
      cleanups.push(() => observer.disconnect());
    } else {
      const updateHeader = () => header.classList.toggle('is-reading', $('.sr-story-hero').getBoundingClientRect().bottom < 104);
      listen(window, 'scroll', updateHeader, { passive: true }); updateHeader();
    }

    function resetMenuAnimation() {
      menuAnimation?.revert(); menuAnimation = null;
      const links = $$('nav a', menu);
      window.gsap?.killTweensOf(links);
      links.forEach(link => ['transform', 'opacity', 'translate', 'rotate', 'scale'].forEach(property => link.style.removeProperty(property)));
    }
    function openDialog(dialog) {
      cancelPendingArtwork();
      cancelGuidance();
      rememberView();
      if (dialog === menu) dialog.returnValue = '';
      lock(); dialog.showModal();
      if (dialog === menu) {
        menuButton.setAttribute('aria-expanded', 'true');
        resetMenuAnimation();
        if (motion && window.gsap) menuAnimation = gsap.fromTo($$('nav a', dialog), { x: 35, opacity: 0 }, { x: 0, opacity: 1, duration: .45, stagger: .065, ease: 'power2.out', clearProps: 'transform,opacity' });
      }
    }
    [menu, credits, photoGallery].forEach(dialog => {
      listen($('[data-close-dialog]', dialog), 'click', () => dialog.close());
      listen(dialog, 'close', () => {
        unlock();
        if (dialog === photoGallery) photoGalleryButton.focus({ preventScroll: true });
        if (dialog !== menu) return;
        resetMenuAnimation(); menuButton.setAttribute('aria-expanded', 'false');
        if (dialog.returnValue === 'navigate') return;
        // Turning motion on at the top can restore the photo-only intro. Keep
        // Explore's return target in the readable part of that same introduction.
        const intro = window.ScrollTrigger?.getById('intro');
        if (header.inert && intro) {
          scrollTo({ top: intro.start + (intro.end - intro.start) * .75, behavior: 'instant' });
          ScrollTrigger.update(); intro.getTween()?.progress(1);
        }
        menuButton.focus({ preventScroll: true });
      });
      listen(dialog, 'click', e => {
        const r = dialog.getBoundingClientRect();
        if (e.target === dialog && (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom)) dialog.close();
      });
    });
    // Wrap through visible controls, including expanded source accordions. Native
    // dialogs otherwise let Tab leave the document for browser chrome at the edge.
    [menu, credits, photoGallery, viewer].forEach(dialog => listen(dialog, 'keydown', e => {
      if (e.key !== 'Tab') return;
      const controls = $$('a[href], button:not(:disabled), select:not(:disabled), summary, [tabindex="0"]', dialog)
        .filter(control => control.getClientRects().length && getComputedStyle(control).visibility !== 'hidden');
      if (!controls.length) return;
      const index = controls.indexOf(document.activeElement);
      const next = index < 0 ? (e.shiftKey ? controls.length - 1 : 0) : (index + (e.shiftKey ? -1 : 1) + controls.length) % controls.length;
      e.preventDefault(); controls[next].focus();
    }));
    listen(menuButton, 'click', () => openDialog(menu));
    listen(photoGalleryButton, 'click', () => openDialog(photoGallery));
    listen($('.pw-source-link'), 'click', () => { credits.close(); frame(() => openDialog(photoGallery)); });
    listen(wallStyleSelect, 'change', () => { wallStyle = wallStyleSelect.value; save('st-raphael-wall-style', wallStyle); build({ preserveScene: true }); });
    $$('[data-open-credits]').forEach(b => listen(b, 'click', () => openDialog(credits)));

    // The responsive healing image sits inside a letterboxed frame. Animate the
    // painted area so enlarging it preserves the painting's proportions.
    function imageBounds(image) {
      const r = image.getBoundingClientRect();
      if (!image.dataset.fullImage) return r;
      if (!image.naturalWidth || !image.naturalHeight) return r;
      const ratio = image.naturalWidth / image.naturalHeight;
      const width = Math.min(r.width, r.height * ratio), height = width / ratio;
      return { left: r.left + (r.width - width) / 2, top: r.top + (r.height - height) / 2, width, height };
    }
    // A failed full-size request can still use the reproduction already on the page.
    // Bound the wait so a stalled request never leaves the artwork control unresponsive.
    function cancelPendingArtwork() {
      if (!openingImage) return;
      imageRequest++; openingImage = false; origin?.removeAttribute('aria-busy');
    }
    listen(document, 'keydown', event => { if (event.key === 'Escape') cancelPendingArtwork(); });
    const decodeArtwork = image => new Promise(resolve => {
      const timer = later(() => resolve(false), 2000);
      image.decode().then(() => { clearTimeout(timer); resolve(image.naturalWidth > 0); },
        () => { clearTimeout(timer); resolve(false); });
    });
    const viewerStatus = document.createElement('p');
    viewerStatus.setAttribute('role', 'status'); viewerStatus.hidden = true;
    $('#image-caption').after(viewerStatus);
    cleanups.push(() => viewerStatus.remove());
    // FLIP keeps an image connected to its original position when opening and closing.
    const rectTransform = (from, to) => ({ x: from.left - to.left, y: from.top - to.top, scaleX: from.width / to.width, scaleY: from.height / to.height });
    $$('[data-view-image]').forEach(button => listen(button, 'click', async event => {
      event.preventDefault();
      if (viewer.open || openingImage) return;
      openingImage = true;
      const request = ++imageRequest;
      origin = button; closing = false;
      const source = $('img', button), image = $('#viewer-image');
      button.setAttribute('aria-busy', 'true');
      viewerStatus.hidden = true; viewerStatus.textContent = ''; image.hidden = false;
      image.src = button.dataset.viewImage; image.alt = source.alt;
      $('#image-caption').textContent = button.dataset.caption;
      let loaded = await decodeArtwork(image);
      if (destroyed || request !== imageRequest) return;
      if (!loaded && source.complete && source.naturalWidth > 0) {
        image.src = source.currentSrc || source.src;
        loaded = await decodeArtwork(image);
      }
      if (destroyed || request !== imageRequest) return;
      button.removeAttribute('aria-busy');
      if (!loaded) {
        image.hidden = true;
        viewerStatus.textContent = 'The image could not load. Please close this viewer and try again.';
        viewerStatus.hidden = false;
      }
      image.width = image.naturalWidth || source.naturalWidth || 1200; image.height = image.naturalHeight || source.naturalHeight || 1600;
      const from = imageBounds(source); lock(); viewer.showModal(); openingImage = false;
      if (loaded && motion && window.gsap) {
        gsap.set(image, { clearProps: 'all' });
        gsap.fromTo(image, rectTransform(from, image.getBoundingClientRect()), { x: 0, y: 0, scaleX: 1, scaleY: 1, duration: .65, ease: 'power3.inOut', clearProps: 'transform' });
        gsap.fromTo($$('#image-caption'), { opacity: 0, y: 10 }, { opacity: 1, y: 0, delay: .35, duration: .3, clearProps: 'transform,opacity' });
      }
    }));
    function closeViewer() {
      if (!viewer.open || closing) return;
      closing = true; const image = $('#viewer-image');
      if (!image.hidden && image.naturalWidth && motion && window.gsap && origin?.isConnected) {
        gsap.killTweensOf(image); gsap.set(image, { clearProps: 'transform' });
        gsap.to($('#image-caption'), { opacity: 0, duration: .15 });
        gsap.to(image, { ...rectTransform(imageBounds($('img', origin)), image.getBoundingClientRect()), duration: .45, ease: 'power3.inOut', onComplete: () => viewer.close() });
      } else viewer.close();
    }
    listen($('.image-viewer-close'), 'click', closeViewer);
    listen(viewer, 'cancel', e => { e.preventDefault(); closeViewer(); });
    listen(viewer, 'click', e => { if (e.target === viewer || e.target === $('figure', viewer)) closeViewer(); });
    listen(viewer, 'close', () => {
      unlock(); closing = false;
      if (window.gsap) gsap.set([$('#viewer-image'), $('#image-caption')], { clearProps: 'all' });
      origin?.focus({ preventScroll: true });
    });

    function goToChapter(index) {
      cancelGuidance();
      const n = Math.max(0, Math.min(panels.length - 1, index));
      const trigger = window.ScrollTrigger?.getById(`photo-${n}`);
      if (trigger && motion) {
        scrollTo({ top: trigger.start + (trigger.end - trigger.start) * .78, behavior: 'instant' });
        ScrollTrigger.update(); trigger.getTween()?.progress(1);
      } else $('.journey-copy', panels[n]).scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    function scrollToSection(target, behavior = 'instant') {
      cancelGuidance();
      const index = panels.indexOf(target);
      if (motion && index >= 0) goToChapter(index);
      else (index >= 0 ? $('.journey-copy', target) : target).scrollIntoView({ behavior, block: 'start' });
    }
    $$('[data-chapter-step]').forEach(b => listen(b, 'click', () => goToChapter(chapter + Number(b.dataset.chapterStep))));
    $$('a[href^="#"]').forEach(link => listen(link, 'click', e => {
      const target = $(link.getAttribute('href')); if (!target) return;
      e.preventDefault(); if (menu.open) menu.close('navigate');
      frame(() => {
        scrollToSection(target, motion ? 'smooth' : 'instant');
        history.replaceState(null, '', link.getAttribute('href'));
        const focus = $('h1,h2', target) || target;
        if (!focus.hasAttribute('tabindex')) focus.tabIndex = -1;
        focus.focus({ preventScroll: true });
      });
    }));
    listen(prayerButton, 'click', () => {
      const opening = prayer.hidden;
      if (window.gsap) gsap.killTweensOf($$('.prayer-line', prayer));
      prayer.hidden = !opening; prayerButton.setAttribute('aria-expanded', String(opening));
      $('span', prayerButton).textContent = opening ? 'Close the prayer' : 'Offer a prayer';
      if (opening) {
        if (motion && window.gsap) gsap.fromTo($$('.prayer-line'), { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: .5, stagger: .07, ease: 'power2.out', clearProps: 'transform,opacity' });
        prayer.focus({ preventScroll: true }); prayer.scrollIntoView({ behavior: motion ? 'smooth' : 'instant', block: 'center' });
      }
      window.ScrollTrigger?.refresh();
    });
    $$('.ministry').forEach(item => listen(item, 'toggle', () => {
      if (item.open && motion && window.gsap) gsap.fromTo($('div', item), { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: .35, clearProps: 'transform,opacity' });
      window.ScrollTrigger?.refresh();
    }));
    function setChapter(index, isolate = false) {
      chapter = index; $('#journey-current').textContent = `0${index + 1}`;
      if (isolate) panels.forEach((p, i) => { p.inert = i !== index; p.setAttribute('aria-hidden', String(i !== index)); });
      $('[data-chapter-step="-1"]').disabled = index === 0; $('[data-chapter-step="1"]').disabled = index === 2;
    }

    function rememberScene(t) {
      if (t?.isActive && !resizing) sceneBookmark = { id: t.vars.id, progress: t.progress, element: t.trigger };
    }
    function build({ preserveScene = false } = {}) {
      if (destroyed) return;
      cancelGuidance();
      const active = preserveScene && window.ScrollTrigger?.getAll().find(t => t.vars.id && /^(intro|photo-|parish$|album$)/.test(t.vars.id) && t.isActive);
      const wallBounds = $('.pw-wall').getBoundingClientRect();
      const visibleWall = preserveScene && wallBounds.top < innerHeight && wallBounds.bottom > 0;
      const visiblePanel = preserveScene && panels.find(p => { const r = p.getBoundingClientRect(); return r.top < innerHeight * .5 && r.bottom > innerHeight * .5; });
      const savedScene = preserveScene && resizing ? resizeBookmark : (active ? { id: active.vars.id, progress: active.progress, element: active.trigger } : visiblePanel ? { id: `photo-${panels.indexOf(visiblePanel)}`, progress: .8, element: visiblePanel } : visibleWall ? { id: 'album', progress: .5, element: $('.pw-wall') } : null);
      const savedView = preserveScene && (resizing ? resizeViewBookmark : viewBookmark);
      // Keep the visible content in place when a setting changes the page height.
      // Use its reveal container, excluding the temporary entrance translation.
      const viewElement = !resizing && savedView?.element ? savedView.element.closest('.reveal') || savedView.element : savedView?.element;
      const viewTop = () => viewElement.getBoundingClientRect().top - (!resizing && window.gsap ? Number(gsap.getProperty(viewElement, 'y')) || 0 : 0);
      const viewOffset = !resizing && viewElement ? viewTop() / innerHeight : savedView?.offset;
      context?.revert();
      panels.forEach(p => { p.inert = false; p.removeAttribute('aria-hidden'); $('.journey-copy', p).inert = false; });
      document.body.classList.remove('reading-mode');
      const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const textTooTall = panels.some(p => $('.journey-copy', p).scrollHeight > innerHeight - 170) || $('.hero-content').scrollHeight > innerHeight - 160;
      cinematic = motion && !!window.gsap && !!window.ScrollTrigger && innerHeight >= 600 && innerWidth >= 360 && rootSize <= 20 && !textTooTall;
      const healingImage = $('#healing .journey-art img');
      if (healingImage.hasAttribute('srcset')) healingImage.sizes = cinematic ? '(max-width: 1023px) 220vw, 100vw' : '(max-width: 767px) 280px, 420px';
      document.body.classList.toggle('reading-mode', !cinematic);
      document.body.classList.toggle('motion-off', !motion);
      document.body.classList.remove('has-cinematic-motion');
      document.body.classList.toggle('photo-motion', cinematic);
      const wallAnimated = motion && !!window.gsap && !!window.ScrollTrigger && innerHeight >= 560 && innerWidth >= 340 && rootSize <= 22;
      document.body.classList.toggle('wall-motion', wallAnimated);
      document.body.dataset.wallStyle = wallStyle;
      document.body.classList.remove('journey-stage');
      document.body.classList.remove('album-animated');
      if (!motion) resetMenuAnimation();
      motionButton.setAttribute('aria-pressed', String(motion));
      motionButton.textContent = motion ? 'Motion on' : 'Motion off';
      motionButton.setAttribute('aria-label', motion ? 'Motion on, turn motion off' : 'Motion off, turn motion on');
      motionButton.disabled = reduced.matches || editor || !observersAvailable;
      motionButton.title = editor ? 'The editor keeps all story content visible.' : !observersAvailable ? 'This browser uses the reading layout.' : reduced.matches ? 'Your device’s Reduce Motion setting is enabled.' : '';
      if (!window.gsap || !window.ScrollTrigger) {
        document.documentElement.classList.remove('js', 'intro-pending');
        document.body.classList.remove('has-cinematic-motion', 'album-animated'); document.body.classList.add('motion-off'); return;
      }
      gsap.registerPlugin(ScrollTrigger); ScrollTrigger.config({ ignoreMobileResize: true });
      context = gsap.context(() => {
        if (wallAnimated) {
          const gentle = wallStyle === 'gentle';
          const travel = (innerWidth < 768 ? 940 : Math.min(Math.min(innerWidth, 1600) * .85, 1450)) * (gentle ? .42 : 1);
          const wallTimeline = gsap.timeline({ scrollTrigger: { id: 'album', trigger: '.pw-wall', start: 'top top', end: 'bottom bottom', scrub: responseTime(.65, .9, .28), invalidateOnRefresh: true, onUpdate: rememberScene } });
          wallTimeline.fromTo('.pw-top', { x: 0, y: 18 }, { x: -travel, y: -24, ease: 'none', duration: 1 }, 0)
            .fromTo('.pw-middle', { x: -travel * .6, y: 0 }, { x: travel * .23, y: 22, ease: 'none', duration: 1 }, 0)
            .fromTo('.pw-bottom', { x: 0, y: 18 }, { x: -travel * .8, y: -20, ease: 'none', duration: 1 }, 0)
            .fromTo('.pw-wall-images', { opacity: gentle ? .55 : .8 }, { opacity: gentle ? .7 : 1, duration: .2, ease: 'none' }, 0)
            .to('.pw-wall-images', { opacity: gentle ? .35 : .6, duration: .14, ease: 'none' }, .86);
        }
        if (!cinematic) {
          gsap.set(['.site-header', '.hero-content', '.reading-progress', '.parish-window-label'], { autoAlpha: 1 });
          header.inert = false; $('.hero-content').inert = false;
          return;
        }
        gsap.to('.reading-progress span', { scaleX: 1, ease: 'none', scrollTrigger: { trigger: document.documentElement, start: 'top top', end: () => Math.max(1, document.documentElement.scrollHeight - innerHeight), scrub: true, invalidateOnRefresh: true } });
        gsap.set(['.site-header', '.hero-content', '.reading-progress'], { autoAlpha: 0 });
        header.inert = true; $('.hero-content').inert = true;
        const intro = gsap.timeline({ scrollTrigger: { id: 'intro', trigger: '.sr-story-hero', start: 'top top', end: 'bottom bottom', scrub: responseTime(.45, .8, .24), snap: softSettle([.83]), invalidateOnRefresh: true }, onUpdate() { rememberScene(this.scrollTrigger); header.inert = this.progress() < (motion ? .4 : .15); $('.hero-content').inert = this.progress() < (motion ? .65 : .15); } });
        if (motion) {
          const geometry = full => {
            const img = $('.hero-art img'), ratio = img.naturalWidth / img.naturalHeight || 1414 / 1800;
            const wide = desktop.matches;
            // The story is framed at most 1600px wide, so size against the scene, not the window.
            const W = $('.hero-scene')?.clientWidth || innerWidth;
            const width = !full && wide ? Math.min(W * .53, innerHeight * ratio) : Math.max(W, innerHeight * ratio);
            const height = width / ratio;
            return { width, height, x: !full && wide ? W - width - W * .025 : (W - width) / 2, y: (innerHeight - height) * .4 };
          };
          intro.fromTo('.hero-art img', { width: () => geometry(true).width, height: () => geometry(true).height, x: () => geometry(true).x, y: () => geometry(true).y }, { width: () => geometry(false).width, height: () => geometry(false).height, x: () => geometry(false).x, y: () => geometry(false).y, duration: .58, ease: 'power1.inOut' }, 0)
            .set('.hero-content', { autoAlpha: 1 }, .38)
            .fromTo('.hero-art-shade', { opacity: 0 }, { opacity: 1, duration: .28 }, .38)
            .fromTo('.title-line:first-child > span', { xPercent: -115, opacity: 0 }, { xPercent: 0, opacity: 1, duration: .26, ease: 'power2.out' }, .43)
            .fromTo('.title-line:last-child > span', { xPercent: 115, opacity: 0 }, { xPercent: 0, opacity: 1, duration: .26, ease: 'power2.out' }, .47)
            .fromTo('.hero-content > .eyebrow', { x: -35, opacity: 0 }, { x: 0, opacity: 1, duration: .2 }, .49)
            .fromTo('.hero-description', { x: -30, opacity: 0 }, { x: 0, opacity: 1, duration: .2 }, .59)
            .fromTo('.round-link', { x: -22, opacity: 0 }, { x: 0, opacity: 1, duration: .18 }, .65)
            .to(['.site-header', '.reading-progress'], { autoAlpha: 1, duration: .12 }, .4)
            .to({}, { duration: .17 }, .83);
        } else {
          intro.set(['.hero-content', '.site-header', '.reading-progress'], { autoAlpha: 1 }, .15).set('.hero-art-shade', { opacity: 1 }, .15).to({}, { duration: .85 }, .15);
        }
        if (!motion) return;
        $$('.reveal').forEach(el => gsap.from(el, { y: 25, opacity: 0, duration: .75, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 92%', once: true } }));
        gsap.from('#name-title', { y: 20, opacity: 0, duration: .65, scrollTrigger: { trigger: '.name-section', start: 'top 85%', once: true } });
        panels.forEach((panel, i) => {
          const copy = $('.journey-copy', panel), art = $('.journey-art', panel);
          const timeline = gsap.timeline({ scrollTrigger: { id: `photo-${i}`, trigger: panel, start: () => $('.journey').getBoundingClientRect().top + scrollY + i * innerHeight * 2.4, end: () => $('.journey').getBoundingClientRect().top + scrollY + (i + 1) * innerHeight * 2.4, scrub: responseTime(.4, .75, .24), snap: softSettle([.34, .82]), invalidateOnRefresh: true, onUpdate: t => { rememberScene(t); copy.inert = t.progress < .48 || t.progress >= .98; panel.inert = !t.isActive; if (t.isActive) setChapter(i); } } });
          gsap.set(panel, { zIndex: i + 1 });
          timeline.fromTo(panel, { xPercent: 100 }, { xPercent: 0, duration: .3, ease: 'power1.inOut' }, 0)
            .fromTo($('.journey-scene', panel), { '--photo-shade': 0 }, { '--photo-shade': 1, duration: .28 }, .38)
            .fromTo(copy, { xPercent: -35, autoAlpha: 0 }, { xPercent: 0, autoAlpha: 1, duration: .3, ease: 'power2.out' }, .44)
            .fromTo($('figcaption', panel), { opacity: 0 }, { opacity: 1, duration: .2 }, .6)
            .to({}, { duration: .2 }, .8);
        });
        gsap.timeline({ scrollTrigger: { id: 'parish', trigger: '.parish-scroll', start: 'top top', end: 'bottom bottom', scrub: responseTime(1, .85, .28), invalidateOnRefresh: true, onUpdate: rememberScene } })
          .fromTo('.parish-image-wrap', { clipPath: () => desktop.matches ? 'inset(6% 20% 0% round 45% 45% 0% 0%)' : 'inset(6% 8% 0% round 45% 45% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% round 0% 0% 0% 0%)', ease: 'power1.inOut', duration: 1 }, 0)
          .fromTo('.parish-image-wrap img', { scale: 1.15 }, { scale: 1, ease: 'none', duration: 1.5 }, 0)
          .fromTo('.parish-window-shade', { opacity: 0 }, { opacity: 1, duration: .5 }, .7)
          .fromTo('.parish-window-label', { opacity: 0, y: 25 }, { opacity: 1, y: 0, duration: .6 }, .85);
        gsap.timeline({ scrollTrigger: { id: 'mission', trigger: '.mission-section', start: 'top 85%', end: 'bottom 5%', scrub: responseTime(.5, .55, .25) } })
          .fromTo('.mission-line:first-child', { xPercent: -10, opacity: 0 }, { xPercent: 0, opacity: 1, duration: .3 }, 0)
          .fromTo('.mission-line:last-child', { xPercent: 10, opacity: 0 }, { xPercent: 0, opacity: 1, duration: .3 }, .06)
          .to('.mission-line:first-child', { xPercent: desktop.matches ? 17 : 6, opacity: 0, duration: .32 }, .72)
          .to('.mission-line:last-child', { xPercent: desktop.matches ? -17 : -6, opacity: 0, duration: .32 }, .78);

      }, root);
      document.documentElement.classList.add('motion-built'); document.documentElement.classList.remove('intro-pending');
      ScrollTrigger.refresh();
      if (savedScene) {
        const rebuilt = ScrollTrigger.getById(savedScene.id);
        if (rebuilt) { scrollTo({ top: rebuilt.start + (rebuilt.end - rebuilt.start) * savedScene.progress, behavior: 'instant' }); ScrollTrigger.update(); rebuilt.getTween()?.progress(1); }
        else (savedScene.id.startsWith('photo-') && savedScene.progress > .48 ? $('.journey-copy', savedScene.element) : savedScene.element).scrollIntoView({ behavior: 'instant', block: 'start' });
      }
      else if (viewElement?.isConnected) {
        scrollBy({ top: viewTop() - viewOffset * innerHeight, behavior: 'instant' });
        ScrollTrigger.update();
        ScrollTrigger.getAll().forEach(t => t.getTween()?.progress?.(1));
      }
    }
    function toggle() { motion = !motion && !reduced.matches && !editor && observersAvailable; save('st-raphael-story-motion', motion ? 'on' : 'off'); build({ preserveScene: true }); }
    listen(motionButton, 'click', toggle);
    const themeSelect = $('#theme-select'), colorScheme = matchMedia('(prefers-color-scheme: dark)');
    let theme = read('st-raphael-story-theme') || 'system';
    const applyTheme = () => { document.documentElement.dataset.theme = theme === 'system' ? (colorScheme.matches ? 'dark' : 'light') : theme; themeSelect.value = theme; };
    listen(themeSelect, 'change', () => { theme = themeSelect.value; save('st-raphael-story-theme', theme); applyTheme(); });
    listen(colorScheme, 'change', applyTheme); applyTheme();
    listen(reduced, 'change', () => { motion = !editor && observersAvailable && !reduced.matches && read('st-raphael-story-motion') !== 'off'; build({ preserveScene: true }); });
    let lastWidth = innerWidth, lastHeight = innerHeight;
    // Track the text or photograph being read, not merely the last animated scene.
    function rememberView() {
      if (resizing || menu.open || credits.open || viewer.open || photoGallery.open) return;
      const active = window.ScrollTrigger?.getAll().find(t => /^(intro|photo-|parish$|album$)/.test(t.vars.id || '') && t.isActive);
      if (active) { viewBookmark = { scene: { id: active.vars.id, progress: active.progress, element: active.trigger }, element: null, offset: 0 }; return; }
      const items = $$('main h1, main h2, main p, main figure, main .ministry, footer').filter(e => !e.closest('[inert]') && getComputedStyle(e).visibility !== 'hidden');
      const visible = items.map(element => ({ element, r: element.getBoundingClientRect() })).filter(x => x.r.width && x.r.bottom > 0 && x.r.top < innerHeight);
      const item = visible.find(x => x.r.top >= 0) || visible.at(-1);
      viewBookmark = { scene: active ? { id: active.vars.id, progress: active.progress, element: active.trigger } : null, element: item?.element, offset: item ? item.r.top / innerHeight : 0 };
      if (!active && item) {
        const panel = item.element.closest('.journey-panel');
        if (panel) viewBookmark.scene = { id: `photo-${panels.indexOf(panel)}`, progress: .8, element: panel };
      }
    }
    listen(window, 'scroll', () => { cancelAnimationFrame(viewFrame); viewFrame = frame(rememberView); }, { passive: true });
    const scheduleLayout = () => {
      cancelGuidance();
      if (!resizing) { resizeBookmark = viewBookmark?.scene || null; resizeViewBookmark = viewBookmark?.element ? viewBookmark : null; }
      resizing = true; clearTimeout(resizeTimer);
      resizeTimer = later(() => { build({ preserveScene: true }); resizing = false; resizeBookmark = null; resizeViewBookmark = null; frame(rememberView); }, 180);
    };
    listen(window, 'resize', () => {
      const widthChanged = Math.abs(innerWidth - lastWidth) > 1;
      const heightChanged = Math.abs(innerHeight - lastHeight) > 120 || matchMedia('(pointer:fine)').matches || (innerHeight < 600) !== (lastHeight < 600) || (innerHeight < 620) !== (lastHeight < 620);
      if (widthChanged || heightChanged) { lastWidth = innerWidth; lastHeight = innerHeight; scheduleLayout(); }
    });
    const fontProbe = document.createElement('span');
    fontProbe.setAttribute('aria-hidden', 'true'); fontProbe.className = 'font-size-probe'; document.body.append(fontProbe);
    let lastFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    cleanups.push(() => fontProbe.remove());
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => { const size = parseFloat(getComputedStyle(document.documentElement).fontSize); if (size !== lastFontSize) { lastFontSize = size; scheduleLayout(); } });
      observer.observe(fontProbe); cleanups.push(() => observer.disconnect());
    }
    // Refresh temporarily rewinds timelines. Restore semantic interactivity from
    // their final positions after that measurement pass, including opened prayers.
    listen(window.ScrollTrigger, 'refresh', () => {
      const intro = ScrollTrigger.getById('intro');
      if (intro) { header.inert = intro.progress < (motion ? .4 : .15); $('.hero-content').inert = intro.progress < (motion ? .65 : .15); }
      panels.forEach((p, i) => { const t = ScrollTrigger.getById(`photo-${i}`); p.inert = !!t && !t.isActive; $('.journey-copy', p).inert = !!t && motion && (t.progress < .48 || t.progress >= .98); });


    });
    document.documentElement.classList.add('js');
    build(); document.fonts?.ready.then(() => { if (!destroyed) build({ preserveScene: true }); });
    function restoreLocation() {
      const target = location.hash && $$('[id]').find(element => element.id === location.hash.slice(1));
      if (target) frame(() => scrollToSection(target));
      else window.scrollTo({ top: 0, behavior: 'instant' });
    }
    listen(window, 'hashchange', restoreLocation);
    listen(window, 'load', () => {
      window.ScrollTrigger?.refresh(); restoreLocation(); frame(rememberView);
    }, { once: true });
    function destroy() {
      if (destroyed) return;
      destroyed = true; imageRequest++; cancelPendingArtwork();
      timers.forEach(clearTimeout); frames.forEach(cancelAnimationFrame);
      resetMenuAnimation(); context?.revert();
      window.gsap?.killTweensOf($$('*'));
      // Remove handlers before close(), so detached dialogs cannot steal focus.
      cleanups.reverse().forEach(cleanup => cleanup());
      [menu, credits, photoGallery, viewer].forEach(dialog => { if (dialog.open) dialog.close(); });
      unlock();
      $$('[inert]').forEach(element => { element.inert = false; });
      panels.forEach(panel => panel.removeAttribute('aria-hidden'));
      document.body.classList.remove('has-cinematic-motion', 'photo-motion', 'wall-motion', 'journey-stage', 'album-animated');
      document.body.classList.add('reading-mode', 'motion-off');
      document.documentElement.classList.remove('intro-pending');
      controllers.delete(root);
    }
    controllers.set(root, destroy);
  }
  function mountWithin(scope) {
    // Be tolerant of integrations that replace markup without an unload event.
    for (const [root, destroy] of controllers) if (!root.isConnected) destroy();
    if (scope.matches?.('[data-sr-story]')) mount(scope);
    scope.querySelectorAll('[data-sr-story]').forEach(mount);
  }
  mountWithin(document);
  document.addEventListener('shopify:section:load', event => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, destroy] of controllers) {
      if (event.target === root || event.target.contains(root)) destroy();
    }
  });
})();
